<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTeamInvitations } from '@/composables/useTeamInvitations'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import type { Invitation } from '@/types/team'

const props = defineProps<{
  organisationId: number
}>()

const emit = defineEmits<{
  sent: [invitation: Invitation]
}>()

const { t } = useI18n()
const { sending, errors, createInvitation } = useTeamInvitations()

const email = ref('')
const role = ref<'admin' | 'owner'>('admin')

async function handleSubmit(): Promise<void> {
  const result = await createInvitation(props.organisationId, {
    email: email.value,
    role: role.value,
  })
  if (result) {
    emit('sent', result)
    email.value = ''
    role.value = 'admin'
  }
}
</script>

<template>
  <div class="rounded-xl border border-slate-700 bg-slate-800/50 p-5">
    <h3 class="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-4">
      {{ t('team.invitations.send_form_heading') }}
    </h3>
    <form novalidate @submit.prevent="handleSubmit">
      <div class="flex flex-col sm:flex-row gap-3">
        <!-- Email field -->
        <div class="flex-1">
          <label for="invite_email" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('team.invitations.email_label') }}
          </label>
          <input
            id="invite_email"
            v-model="email"
            type="email"
            required
            maxlength="255"
            :placeholder="t('team.invitations.email_placeholder')"
            class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
            :class="errors['email'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
          />
          <p v-if="errors['email']" class="mt-1 text-xs text-red-400">
            {{ errors['email'][0] }}
          </p>
        </div>

        <!-- Role selector -->
        <div class="sm:w-36">
          <label for="invite_role" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('team.invitations.role_label') }}
          </label>
          <select
            id="invite_role"
            v-model="role"
            class="block w-full rounded-lg border border-slate-600 px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 focus:border-emerald-500 focus:ring-emerald-500 transition-colors"
          >
            <option value="admin">{{ t('team.roles.admin') }}</option>
            <option value="owner">{{ t('team.roles.owner') }}</option>
          </select>
        </div>

        <!-- Submit button -->
        <div class="sm:pt-5">
          <button
            type="submit"
            :disabled="sending"
            class="w-full sm:w-auto inline-flex items-center justify-center rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-4 py-2 text-sm transition-colors disabled:opacity-50"
          >
            <AppSpinner v-if="sending" size="button" class="-ml-1 mr-2" />
            {{ sending ? t('common.loading') : t('team.invitations.send_button') }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>
